<article class="ts-page">
  <header class="ts-page__head">
    <p class="t-meta ts-page__eyebrow">Legal</p>
    <h1 class="ts-page__title">Privacy policy</h1>
    <p class="ts-page__lede">Your privacy is crucial while weaving stories with Talespinner.</p>
  </header>

  <div class="ts-doc">
    <nav class="ts-doc__toc" aria-label="Contents">
      <p class="t-meta">Contents</p>
      <ol>
        <li><a [routerLink]="[]" fragment="information-collection">Information Collection</a></li>
        <li><a [routerLink]="[]" fragment="use-of-information">Use of Information</a></li>
        <li><a [routerLink]="[]" fragment="content-privacy">Content Privacy</a></li>
        <li><a [routerLink]="[]" fragment="data-sharing">Data Sharing</a></li>
        <li><a [routerLink]="[]" fragment="data-security">Data Security</a></li>
        <li><a [routerLink]="[]" fragment="your-rights-and-choices">Your Rights and Choices</a></li>
        <li><a [routerLink]="[]" fragment="children-s-privacy">Children's Privacy</a></li>
        <li><a [routerLink]="[]" fragment="changes-to-privacy-policy">Changes to Privacy Policy</a></li>
        <li><a [routerLink]="[]" fragment="contact-us">Contact Us</a></li>
      </ol>
    </nav>

    <div class="ts-prose">
      <p>At Talespinner, we value your privacy and are committed to protecting your personal information. This Privacy Policy outlines our practices concerning the collection, use, and safeguarding of your data when you use our AI-powered storytelling platform.</p>

      <section class="ts-prose__section" id="information-collection">
        <span class="ts-prose__number">§ 01</span>
        <h2>Information Collection</h2>
        <ul>
          <li><strong>Account Information</strong>: When you register, we collect your email address to create and manage your account, and to send you important updates or confirmations.</li>
          <li><strong>Payment Information</strong>: If you purchase Quills, our virtual currency, we collect payment information. This is processed securely through our payment provider, Stripe. Please refer to <a href="https://stripe.com/nl/privacy" target="_blank">Stripe's Privacy Policy</a> for more information on how they handle your payment data.</li>
          <li><strong>Usage Information</strong>: We collect data about your interactions with our service, including the AI models you use, the number of Quills spent, and general usage patterns.</li>
          <li><strong>User-Generated Content</strong>: The stories, characters, plots, and other content you create using our platform are stored to provide you with the service.</li>
        </ul>
      </section>

      <section class="ts-prose__section" id="use-of-information">
        <span class="ts-prose__number">§ 02</span>
        <h2>Use of Information</h2>
        <ul>
          <li>To provide and maintain our service, including generating AI-powered content based on your inputs.</li>
          <li>To process your payments for Quills.</li>
          <li>To send you service updates, important notifications, or to communicate regarding your account.</li>
          <li>To improve and optimize our service, including refining our AI models and user experience.</li>
          <li>To detect, prevent, and address technical issues or potential misuse of our service.</li>
        </ul>
      </section>

      <section class="ts-prose__section" id="content-privacy">
        <span class="ts-prose__number">§ 03</span>
        <h2>Content Privacy</h2>
        <ul>
          <li>The content of your stories remains your private property unless you choose to make a project public.</li>
          <li>Public projects may be visible to other users on the platform when all chapters are finished.</li>
          <li>We do not claim ownership over the content you generate using our service.</li>
        </ul>
      </section>

      <section class="ts-prose__section" id="data-sharing">
        <span class="ts-prose__number">§ 04</span>
        <h2>Data Sharing</h2>
        <ul>
          <li>We may share anonymized, aggregated data with our AI model providers to improve the quality of the service.</li>
          <li>We do not sell your personal information or the content of your stories to third parties.</li>
          <li>We may share information if required by law or to protect our rights or the rights of others.</li>
          <li>When you use our service, your inputs may be processed by our AI service providers. Please refer to their respective privacy policies for more information:
            <ul>
              <li><a href="https://www.anthropic.com/legal/privacy" target="_blank">Anthropic's Privacy Policy</a></li>
              <li><a href="https://privacy.openai.com/policies" target="_blank">OpenAI's Privacy Policy</a></li>
              <li><a href="https://openrouter.ai/privacy" target="_blank">OpenRouter's Privacy Policy</a></li>
            </ul>
          </li>
        </ul>
      </section>

      <section class="ts-prose__section" id="data-security">
        <span class="ts-prose__number">§ 05</span>
        <h2>Data Security</h2>
        <ul>
          <li>We employ robust security measures to protect your personal information and story content from unauthorized access, disclosure, alteration, or destruction.</li>
          <li>While we strive to use commercially acceptable means to protect your data, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</li>
        </ul>
      </section>

      <section class="ts-prose__section" id="your-rights-and-choices">
        <span class="ts-prose__number">§ 06</span>
        <h2>Your Rights and Choices</h2>
        <ul>
          <li>You can access, update, or delete your personal information at any time through your account settings.</li>
          <li>You can choose to make your projects public or keep them private.</li>
          <li>You can opt-out of marketing communications while still receiving essential service notifications.</li>
        </ul>
      </section>

      <section class="ts-prose__section" id="children-s-privacy">
        <span class="ts-prose__number">§ 07</span>
        <h2>Children's Privacy</h2>
        <p>Talespinner is intended for users who are at least 13 years old. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.</p>
      </section>

      <section class="ts-prose__section" id="changes-to-privacy-policy">
        <span class="ts-prose__number">§ 08</span>
        <h2>Changes to Privacy Policy</h2>
        <ul>
          <li>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "last modified" date.</li>
          <li>For significant changes, we will provide a more prominent notice, which may include an email notification.</li>
        </ul>
      </section>

      <section class="ts-prose__section" id="contact-us">
        <span class="ts-prose__number">§ 09</span>
        <h2>Contact Us</h2>
        <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at <a href="mailto:privacy@talespinner.io">privacy&#64;talespinner.io</a>.</p>
      </section>

      <p class="ts-legal__closing">By using Talespinner, you agree to the collection and use of information in accordance with this Privacy Policy.</p>

      <p class="ts-legal__back">Back to the <a routerLink="/account/auth/signup">Registration form</a></p>
    </div>
  </div>
</article>
